<template>
  <div v-if="entitlementBlocked" class="settings-page" />
  <div v-else class="settings-page">
    <div class="agents-header">
      <div>
        <h2>Agents</h2>
        <div class="subtitle">
          Enable and configure AI agents for your organization.
        </div>
      </div>
      <BaseButton
        kind="main"
        type="primary"
        text="New"
        @click="openCreateModal"
      >
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </div>

    <section class="custom-section">
      <div class="section-heading-wrapper" v-if="ourAgents.length !== 0">
        <h3 class="section-heading">Workforce</h3>
      </div>

      <div v-if="ourAgents.length === 0" class="agents-empty">
        <div class="agents-empty-icon">
          <AgentIcon />
        </div>
        <p class="agents-empty-title">No agents yet</p>
        <p class="agents-empty-desc">
          Enable an agent from the catalog below, or create a custom one
          tailored to your workflow.
        </p>
        <BaseButton
          kind="main"
          type="secondary"
          text="New agent"
          @click="openCreateModal"
        >
          <template #icon>
            <AddIcon />
          </template>
        </BaseButton>
      </div>

      <div v-else class="agents-list">
        <template v-for="(agent, agentIndex) in ourAgents" :key="agent.id">
          <div v-if="agentIndex > 0" class="row-separator" />
          <div
            class="agent-row data-row clickable"
            @click="
              agent.type === 'catalog'
                ? handleNavigateToAgent(agent.catalogKey)
                : navigateToAgentById(agent.id)
            "
          >
            <div class="agent-row-avatar">
              <img
                v-if="agent.type === 'catalog' && agent.avatar"
                :src="agent.avatar"
                :alt="agent.name"
                class="agent-row-avatar-img"
              />
              <AgentAvatar
                v-else-if="agent.type === 'custom' && agent.avatarKey"
                :avatar-key="agent.avatarKey"
                :size="36"
              />
              <div v-else class="agent-row-avatar-placeholder">
                {{ (agent.name || "?").charAt(0) }}
              </div>
            </div>
            <div class="agent-row-info">
              <span class="agent-row-name">{{ agent.name }}</span>
              <span class="agent-row-role">{{
                agent.type === "catalog" ? agent.description : "Custom"
              }}</span>
            </div>
            <div class="agent-row-actions" @click.stop>
              <BaseButton
                kind="auxiliary"
                type="tertiary"
                :text="isAgentPinned(agent.id) ? 'Unpin' : 'Pin'"
                @click="togglePin(agent.id)"
              >
                <template #icon>
                  <PinIcon :pinned="isAgentPinned(agent.id)" />
                </template>
              </BaseButton>
              <ButtonMenu placement="bottom-end">
                <template #btn>
                  <BaseButton kind="auxiliary" type="tertiary">
                    <template #icon>
                      <MoreIcon />
                    </template>
                  </BaseButton>
                </template>
                <template #content>
                  <BaseButton
                    kind="auxiliary"
                    type="tertiary"
                    text="Open"
                    is-expanded
                    @click="
                      agent.type === 'catalog'
                        ? handleNavigateToAgent(agent.catalogKey)
                        : navigateToAgentById(agent.id)
                    "
                  />
                  <BaseButton
                    v-if="agent.type === 'catalog'"
                    kind="auxiliary"
                    type="tertiary"
                    tint="red"
                    text="Disable"
                    is-expanded
                    @click="toggleAgent(agent.catalogKey)"
                  />
                  <BaseButton
                    v-else
                    kind="auxiliary"
                    type="tertiary"
                    tint="red"
                    text="Delete"
                    is-expanded
                    @click="deleteCustomAgent(agent.id)"
                  />
                </template>
              </ButtonMenu>
            </div>
          </div>
        </template>
      </div>
    </section>

    <section class="browse-section">
      <h3 class="section-heading">Browse agents</h3>
      <p class="section-desc">
        Discover pre-built agents you can enable for your organization.
      </p>
      <div class="agents-grid">
        <div
          v-for="entry in browseCatalogEntries"
          :key="entry.key"
          class="agent-card clickable"
          @click="openCatalogModal(entry)"
        >
          <div class="agent-avatar">
            <img
              v-if="entry.avatar"
              :src="entry.avatar"
              :alt="entry.name"
              class="avatar-img"
            />
            <div v-else class="avatar-placeholder">
              {{ entry.name.charAt(0) }}
            </div>
          </div>
          <div class="agent-info">
            <div class="agent-name-row">
              <h3 class="agent-name">{{ entry.name }}</h3>
            </div>
            <p class="agent-role">{{ entry.description }}</p>
          </div>
        </div>

        <div
          class="agent-card agent-card-new clickable"
          @click="openCreateModal"
        >
          <div class="agent-card-new-inner">
            <div class="agent-card-new-plus" aria-hidden="true">
              <AddIcon />
            </div>
            <div class="agent-info">
              <div class="agent-name-row">
                <h3 class="agent-name">Custom</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, toRef, watch } from "vue";
import { useModal } from "vue-final-modal";

import type { WorkspaceId } from "@ctrl/controller-rpc";

import AgentAvatar from "@/components/ai/AgentAvatar.vue";
import CatalogAgentModal from "@/components/ai/CatalogAgentModal.vue";
import CreateAgentModal from "@/components/ai/CreateAgentModal.vue";
import type { CatalogAgent, CatalogKey } from "@/components/ai/useAgents";
import BaseButton from "@/components/base/BaseButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import AgentIcon from "@/components/icons/AgentIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  name: "Agents",
  components: {
    AddIcon,
    AgentAvatar,
    AgentIcon,
    BaseButton,
    ButtonMenu,
    MoreIcon,
    PinIcon,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const controller = inject.controller();
    const logger = inject.logger("Agents");
    const router = inject.router();
    const agents = inject.agents();
    const menu = inject.menu();

    logger.debug("setup", agents);

    const orgId = toRef(props, "orgId");

    const billing = inject.billing();
    const isEntitledToAgents = computed(
      () =>
        billing.value?.isEntitledTo("ai-agents", orgId.value)?.value ?? false
    );
    const entitlementBlocked = computed(() => !isEntitledToAgents.value);

    watch(
      () => billing.value?.isEntitledTo("ai-agents", orgId.value)?.value,
      (entitled) => {
        if (entitled === false) {
          openUpgradeModal(billing, orgId.value, "ai-agents");
        }
      },
      { immediate: true }
    );

    const releaser = useReleaser();
    const defaultWorkspaceId = await releaser.add(
      bindAsync(orgId, async (id) => {
        if (!id) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, id);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );

    const catalogEntries = computed(() => {
      const catalog = agents.value?.catalog ?? {};
      return Object.values(catalog);
    });

    const browseCatalogEntries = computed(() =>
      catalogEntries.value.filter((e) => e.tag !== "System")
    );

    const countActive = (key: CatalogKey): number => {
      if (!agents.value) return 0;
      try {
        return agents.value.countActive(orgId.value, key);
      } catch {
        return 0;
      }
    };

    const toggleAgent = async (key: CatalogKey) => {
      if (!agents.value) return;
      const catalogEntry = agents.value.catalog[key];
      if (!catalogEntry) {
        window.console.error(
          `[agents] agent catalog entry with key "${key}" not found`
        );
        return;
      }

      const count = countActive(key);
      if (count > 0) {
        const first = agents.value.listActive(orgId.value, key)[0];
        if (!first) return;
        await agents.value.destroy(orgId.value, first.id);
      } else {
        await agents.value.instantiate(orgId.value, key);
      }
    };

    const handleNavigateToAgent = async (agentKey: CatalogKey) => {
      if (!agents.value) return;
      const list = agents.value.listActive(orgId.value, agentKey);
      const agent = list[0];
      if (!agent) return;
      await navigateTo(RouteNames.Agent, router, {
        params: { agentId: agent.id },
      });
    };

    const navigateToAgentById = async (agentId: string) => {
      await navigateTo(RouteNames.Agent, router, {
        params: { agentId },
      });
    };

    type OurAgent =
      | {
          type: "catalog";
          id: string;
          name: string;
          avatar: string;
          description: string;
          catalogKey: CatalogKey;
        }
      | { type: "custom"; id: string; name: string; avatarKey?: string };

    const ourAgents = computed((): OurAgent[] => {
      if (!agents.value) return [];
      const result: OurAgent[] = [];
      try {
        for (const entry of browseCatalogEntries.value) {
          const active = agents.value.listActive(orgId.value, entry.key);
          if (active.length > 0) {
            result.push({
              type: "catalog",
              id: active[0].id,
              name: entry.name,
              avatar: entry.avatar,
              description: entry.description,
              catalogKey: entry.key,
            });
          }
        }
        for (const agent of agents.value.listCustom(orgId.value)) {
          result.push({
            type: "custom",
            id: agent.id,
            name: agent.name || "Unnamed agent",
            avatarKey: agent.avatar,
          });
        }
      } catch {
        // not yet ready
      }
      return result;
    });

    const deleteCustomAgent = async (agentId: string) => {
      if (!agents.value) return;
      await agents.value.destroy(orgId.value, agentId);
    };

    const { open: openCreateModal } = useModal({
      component: CreateAgentModal,
      attrs: {
        onConfirm: async (name: string, avatarKey: string) => {
          if (!agents.value) return;
          const agentId = await agents.value.create(
            orgId.value,
            name,
            avatarKey
          );
          if (agentId) {
            await navigateToAgentById(agentId);
          }
        },
      },
    });

    const isAgentPinned = (agentId: string): boolean =>
      menu.isItemPinned(agentId);

    const togglePin = async (agentId: string) => {
      if (isAgentPinned(agentId)) {
        const item = menu.visibleItems.value.find(
          (i) => i.target.kind === "agent" && i.target.agent_id === agentId
        );
        if (item) await menu.unpinAgent(item);
      } else {
        await menu.pinAgent(agentId, orgId.value);
      }
    };

    const openCatalogModal = (entry: CatalogAgent) => {
      const { open } = useModal({
        component: CatalogAgentModal,
        attrs: {
          entry,
          isActive: countActive(entry.key) > 0,
          onEnable: async () => {
            await toggleAgent(entry.key);
          },
          onOpen: async () => {
            await handleNavigateToAgent(entry.key);
          },
          onDisable: async () => {
            await toggleAgent(entry.key);
          },
        },
      });
      open();
    };

    return {
      entitlementBlocked,
      toggleAgent,
      handleNavigateToAgent,
      navigateToAgentById,
      browseCatalogEntries,
      ourAgents,
      countActive,
      deleteCustomAgent,
      defaultWorkspaceId,
      openCreateModal,
      openCatalogModal,
      isAgentPinned,
      togglePin,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.agents-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  h2 {
    margin-bottom: 4px;
  }

  .subtitle {
    margin: 0;
  }
}

// ── Sections ──────────────────────────────────────────────────────────────────

.browse-section,
.custom-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 24px;
}

.section-heading-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-heading {
  margin: 0;
  @extend %font-h4;

  @include themed() {
    color: t($primary);
  }
}

.section-desc {
  margin: 0;
  margin-bottom: 12px;
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-60);
  }
}

.agents-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px;
  border-radius: 12px;
  gap: 6px;

  @include themed() {
    background-color: t($bg-alt);
    border: 1px solid t($grey-10);
  }

  .agents-empty-title {
    margin: 0;
    @extend %font-body-medium;

    @include themed() {
      color: t($primary);
    }
  }

  .agents-empty-desc {
    margin: 0 0 12px;
    @extend %font-body-regular;
    max-width: 320px;

    @include themed() {
      color: t($grey-60);
    }
  }
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100%;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.agent-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  border-radius: 8px;
  min-width: 0;
  @include transition(border-color 0.1s ease-out);

  @include themed() {
    border: 1px solid t($grey-15);
  }

  &.clickable:hover {
    @include themed() {
      border-color: t($primary);
    }
  }
}

.agent-card-new {
  /* Keep intrinsic height like other cards; center block via inner + margin auto. */
  .agent-card-new-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: auto;
    margin-bottom: auto;
    width: 100%;
  }

  .agent-info {
    justify-content: center;
  }

  .agent-name-row {
    margin-bottom: 0;
  }
}

.agent-card-new-plus {
  width: 52px;
  height: 52px;
  border-radius: $border-radius-l;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @include themed() {
    background-color: t($primary);
    color: t($bg);
  }

  :deep(svg) {
    width: 28px;
    height: 28px;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: currentColor;
    }
  }
}

.agent-avatar {
  width: 70px;
  height: 70px;
  border-radius: $border-radius-l;
  overflow: hidden;
  margin-bottom: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    @extend %font-h3;
    font-weight: 500;

    @include themed() {
      background-color: t($tertiary-50);
      color: t($bg);
    }
  }
}

.agent-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex-grow: 1;
}

.agent-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.agent-name {
  @extend %font-body-medium;
  margin: 0;

  @include themed() {
    color: t($primary);
  }
}

.agent-role {
  @extend %font-caption-regular;
  margin: 0;

  @include themed() {
    color: t($grey-60);
  }
}

// ── Custom agents list ────────────────────────────────────────────────────────

.agents-list {
  display: flex;
  flex-direction: column;
}

.agent-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-row-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .agent-row-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .agent-row-avatar-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    @extend %font-body-medium;

    @include themed() {
      background-color: t($tertiary-50);
      color: t($bg);
    }
  }
}

.agent-row-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.agent-row-name {
  @extend %font-body-regular;

  @include themed() {
    color: t($primary);
  }
}

.agent-row-role {
  @extend %font-caption-regular;

  @include themed() {
    color: t($grey-60);
  }
}

.agent-row-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
</style>
